import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import { User, LogOut, ChevronRight, MapPin, Compass } from "lucide-react";
import { toast } from "sonner";

export default function AccountPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    toast.success("À bientôt, voyageur !");
    navigate("/");
  };

  return (
    <div className="min-h-screen" style={{ background: "#F9F6F0" }}>
      <Navbar />
      <main className="max-w-2xl mx-auto px-5 sm:px-8 py-10">
        <p className="section-label mb-2">Paramètres</p>
        <h1
          className="text-4xl mb-8"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}
        >
          Mon compte
        </h1>

        {/* Profile card */}
        <div className="card-surface mb-5 animate-fade-in-up" data-testid="account-profile-card">
          <div className="flex items-center gap-4">
            <div
              className="w-14 h-14 bg-[#C17F59] rounded-full flex items-center justify-center text-[#F9F6F0] text-xl"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }}
            >
              {user?.name?.[0]?.toUpperCase() || "V"}
            </div>
            <div className="flex-1 min-w-0">
              <h2
                className="text-xl truncate"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}
                data-testid="account-name"
              >
                {user?.name}
              </h2>
              <p className="text-sm text-[#5C554E] truncate italic" style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="account-email">
                {user?.email}
              </p>
            </div>
            {user?.role === "admin" && (
              <span className="text-xs font-medium px-3 py-1 bg-[#C17F59] text-[#F9F6F0] rounded-sm" data-testid="account-plan-badge">
                Admin
              </span>
            )}
          </div>
        </div>

        {/* Service info */}
        <div className="card-surface mb-5 animate-fade-in-up delay-100" data-testid="account-plan-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>
              Le service Voyage-r
            </h3>
          </div>
          <div className="space-y-3">
            {[
              "Itinéraire personnalisé jour par jour, conçu sur mesure",
              "Validation humaine par Diego, expert voyage",
              "Conseiller voyage dédié (offre Sérénité)",
              "Budget détaillé, infos pratiques, météo",
              "Liens de réservation pré-remplis",
            ].map((f, i) => (
              <div key={i} className="flex items-start gap-3 text-sm text-[#5C554E]" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                <Compass size={14} strokeWidth={1.5} className="text-[#C17F59] mt-0.5 flex-shrink-0" />
                {f}
              </div>
            ))}
          </div>
          <div className="border-t border-[#DCD4C6] mt-5 pt-4">
            <p className="text-sm text-[#5C554E] italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
              Chaque voyage est débloqué individuellement — à partir de <strong className="not-italic text-[#2B2623]">29 €</strong>.
            </p>
          </div>
        </div>

        {/* Quick actions */}
        <div className="card-surface mb-5 animate-fade-in-up delay-200" data-testid="account-actions">
          <h3 className="text-lg mb-3" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>
            Actions rapides
          </h3>
          <div className="space-y-1">
            <button
              onClick={() => navigate("/plan")}
              className="flex items-center justify-between w-full px-3 py-3 hover:bg-[#F9F6F0] transition-colors text-sm"
              style={{ fontFamily: "'Lora', Georgia, serif" }}
              data-testid="account-plan-trip-btn"
            >
              <span className="flex items-center gap-2 text-[#2B2623]">
                <MapPin size={14} strokeWidth={1.5} className="text-[#C17F59]" /> Planifier un nouveau voyage
              </span>
              <ChevronRight size={14} strokeWidth={1.5} className="text-[#9A8F86]" />
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center justify-between w-full px-3 py-3 hover:bg-[#F9F6F0] transition-colors text-sm"
              style={{ fontFamily: "'Lora', Georgia, serif" }}
              data-testid="account-dashboard-btn"
            >
              <span className="flex items-center gap-2 text-[#2B2623]">
                <User size={14} strokeWidth={1.5} className="text-[#C17F59]" /> Mes voyages
              </span>
              <ChevronRight size={14} strokeWidth={1.5} className="text-[#9A8F86]" />
            </button>
          </div>
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 py-3 border border-red-200 text-red-600 hover:bg-red-50 transition-colors text-sm animate-fade-in-up delay-300"
          style={{ fontFamily: "'Lora', Georgia, serif", borderRadius: "2px" }}
          data-testid="account-logout-btn"
        >
          <LogOut size={14} strokeWidth={1.5} /> Se déconnecter
        </button>
      </main>
    </div>
  );
}
